import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { api, apiFile, ApiError } from "../api";
import { formatAccountCode, formatMoney, parseMoneyInput } from "../formatting";
import { MoneyInput } from "../components/MoneyInput";
import { InvoicePreviewModal } from "../components/InvoicePreviewModal";
import { FullscreenProgress } from "../components/FullscreenProgress";
import { AtikQuestion } from "./AtikQuestion";
import { initialAtikDraft, type AtikDraft } from "./atikFacts";

const money = (value: string) => formatMoney(value, "TRY", "tr-TR");

type LoadType = "direct" | "general" | "atik";
type Parts = Record<LoadType, string>;
type LateDocumentFacts = {
  late_document_kind?: string; underlying_supply_date?: string;
  correction_ack?: boolean; late_document_explanation?: string;
};
type ReviewIssue = { invoice_line_id: number; load_type: "general" | "atik"; message: string };
type AssetFacts = {
  asset_reference?: string; use_start_period?: string; unrelieved_input_vat?: string;
  opening_prior_allocated_vat?: string; opening_cumulative_refund_turnover?: string;
  opening_cumulative_total_turnover?: string; turnover_scope_confirmed?: boolean;
};
type AtikCalculation = {
  period: string; eligible_vat: string; asset_input_vat: string;
  cumulative_refund_turnover: string; cumulative_total_turnover: string;
  cumulative_ratio: string; cumulative_eligible_vat: string;
  prior_allocated_vat: string; available_room_after_other_atik: string;
};
type Row = LateDocumentFacts & {
  invoice_id: number; invoice_line_id: number; invoice_no: string; supplier: string; item_name: string;
  invoice_date: string; deduction_period?: string; account_codes: string[]; vat_amount: string; quantity: string; unit: string;
  parts: Parts | null; excluded: string[]; period: string; direct_quantity: string;
  explanation: string; atik_facts?: AssetFacts; errors: Record<string, string>;
  eligible: Record<string, string>; stale: boolean; source_changed?: boolean;
  atik_calculation?: AtikCalculation[];
  period_allocations?: { period: string; source_vat_share: string }[];
  automatic_general_periods?: { period: string; source_vat_share: string }[];
  general_period_explanation?: string;
};
export type LoadingReviewState = {
  prepared: boolean; completed: boolean; stale?: boolean; period_start?: string; confirmed: Record<string, boolean>; rows: Row[];
};
type Props = { onDraftsChange?: (rows: Record<string, unknown>[]) => void; caseId: number; review: LoadingReviewState; disabled: boolean; onChange: (review?: LoadingReviewState) => Promise<void> };
type QuickSave = { status: "queued" | "saving" | "saved" | "error"; message: string; draft?: Record<string, unknown> };

export function LoadingReview439({ caseId, review: savedReview, disabled, onChange, onDraftsChange }: Props) {
  const [draftRows, setDraftRows] = useState<Record<number, Record<string, unknown>>>({});
  const review = { ...savedReview, rows: savedReview.rows.map(row => draftRows[row.invoice_line_id]
    ? { ...row, ...draftRows[row.invoice_line_id] } as Row : row) };
  const draftKey = JSON.stringify(draftRows);
  useEffect(() => { onDraftsChange?.(Object.values(draftRows)); }, [draftKey]);
  function stageRows(rows: Record<string, unknown>[]) {
    setDraftRows(current => ({ ...current, ...Object.fromEntries(rows.map(row => [Number(row.line_id), { ...current[Number(row.line_id)], ...row }])) }));
  }
  const [otherOpen, setOtherOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [preview, setPreview] = useState<Row | null>(null);
  const [adding, setAdding] = useState<"general" | "atik">();
  const [busy, setBusy] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [error, setError] = useState("");
  const [excelMessage, setExcelMessage] = useState("");
  const [serverIssues, setServerIssues] = useState<ReviewIssue[]>([]);
  const [checksKind, setChecksKind] = useState<"general" | "atik" | null>(null);
  const [atikOpen, setAtikOpen] = useState(false);
  const [quickSaves, setQuickSaves] = useState<Record<number, QuickSave>>({});
  const [quickBusy, setQuickBusy] = useState(false);
  const quickQueue = useRef<{ row: Row; kind?: LoadType; values?: Record<string, unknown> }[]>([]);
  const quickRunning = useRef(false);
  const quickSelected = useRef(new Set<number>());
  const locked = disabled || busy || quickBusy;
  const labels = { direct: "1 No.lu", general: "2 No.lu", atik: "3 No.lu" };
  const rowError = (row: Row, kind: "general" | "atik") => serverIssues.find(issue => issue.invoice_line_id === row.invoice_line_id && issue.load_type === kind)?.message || row.errors[kind];
  const viewInvoice = (row: Row) => <button type="button" className="text-button loading-review-view" aria-label={`${row.invoice_no} faturasını gör`} onClick={() => setPreview(row)}>Gör</button>;

  useEffect(() => {
    if (!quickBusy) return;
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [quickBusy]);

  function includeWhole(row: Row, kind: LoadType) {
    if (disabled || busy || changed || editing?.invoice_line_id === row.invoice_line_id || quickSelected.current.has(row.invoice_line_id)) return;
    if (onDraftsChange) {
      stageRows([{ line_id: row.invoice_line_id,
        parts: { direct: "0", general: "0", atik: "0", [kind]: row.vat_amount }, excluded: [],
        explanation: row.explanation || "", period: row.invoice_date.slice(0, 7), period_allocations: null,
        atik_facts: kind === "atik" ? row.atik_facts || {} : {} }]);
      return;
    }
    quickSelected.current.add(row.invoice_line_id);
    quickQueue.current.push({ row, kind });
    setQuickSaves(current => ({ ...current, [row.invoice_line_id]: { status: "queued", message: `${labels[kind]} için sırada…` } }));
    void saveQueuedRows();
  }

  function saveEditor(values: Record<string, unknown>) {
    if (!editing || disabled || busy || quickSelected.current.has(editing.invoice_line_id)) return;
    if (onDraftsChange) { stageRows([values]); setEditing(null); return; }
    if (!quickRunning.current) {
      void action("edit_loading_review", values);
      return;
    }
    quickSelected.current.add(editing.invoice_line_id);
    quickQueue.current.push({ row: editing, values });
    setQuickSaves(current => ({ ...current, [editing.invoice_line_id]: { status: "queued", message: "Paylar kaydedilmek üzere sırada…", draft: values } }));
    setEditing(null);
  }

  async function saveQueuedRows() {
    if (quickRunning.current) return;
    quickRunning.current = true;
    setQuickBusy(true);
    // One request at a time for this case; other rows can still be queued.
    do {
      while (quickQueue.current.length) {
        const { row, kind, values } = quickQueue.current.shift()!;
        const id = row.invoice_line_id;
        const update = (status: QuickSave["status"], message: string) => setQuickSaves(current => ({ ...current, [id]: { status, message, draft: values } }));
        update("saving", kind ? `${labels[kind]} için kaydediliyor…` : "Paylar kaydediliyor…");
        try {
          const period = row.invoice_date.slice(0, 7);
          const result = await api<{ loading_review: LoadingReviewState }>(`/api/refund-cases/${caseId}/439-workflow`, {
            method: "POST", body: JSON.stringify({
              action: "edit_loading_review", ...(values || { line_id: id,
              parts: { direct: "0", general: "0", atik: "0", [kind!]: row.vat_amount },
              excluded: [], explanation: row.explanation || "", period,
              period_allocations: null,
              atik_facts: kind === "atik" ? {
                ...row.atik_facts, asset_reference: row.atik_facts?.asset_reference || `${row.invoice_no}-${id}`,
                use_start_period: period,
                turnover_scope_confirmed: row.atik_facts?.use_start_period === period && row.atik_facts?.turnover_scope_confirmed === true,
              } : {} }),
            }),
          });
          const saved = result.loading_review.rows.find(item => item.invoice_line_id === id);
          const issue = Object.values(saved?.errors || {}).join(" ");
          update("saved", `${kind === "direct" ? `${money(row.vat_amount)} kaynak KDV 1 No.luya ayrıldı. Yüklenim hesabı sonraki aşamada yapılacak.` : kind ? `${labels[kind]} listesine ${money(row.vat_amount)} kaydedildi (${period}).` : "Paylar kaydedildi."}${issue ? ` İnceleme gerekiyor: ${issue}` : ""}`);
          setServerIssues(current => current.filter(item => item.invoice_line_id !== id));
        } catch (reason) {
          quickSelected.current.delete(id);
          update("error", reason instanceof Error ? reason.message : "Kayıt tamamlanamadı. Yeniden deneyebilirsiniz.");
        }
      }
      try {
        await onChange();
        setQuickSaves(current => Object.fromEntries(Object.entries(current).filter(([, item]) => item.status !== "saved")));
      }
      catch { setError("Kayıt işlemleri sonuçlandı ancak listeler yenilenemedi. Güncel durumu görmek için sayfayı yenileyin."); }
      // More rows may have been selected while the lists were refreshing.
    } while (quickQueue.current.length);
    quickRunning.current = false;
    quickSelected.current.clear();
    setQuickBusy(false);
  }

  async function action(actionName: string, values: Record<string, unknown> = {}) {
    if (busy || disabled || quickRunning.current) return;
    if (onDraftsChange && actionName === "calculate_loading_review_atik") { stageRows(values.rows as Record<string, unknown>[]); return; }
    if (onDraftsChange && actionName === "edit_loading_review") { stageRows([values]); return; }
    setProgressMessage(actionName === "calculate_loading_review_atik" ? "ATİK payları topluca hesaplanıyor ve 3 No.lu liste kontrol ediliyor…"
      : actionName === "edit_loading_review" ? "Karar kaydediliyor ve yüklenim payları hesaplanıyor…"
      : actionName === "confirm_loading_review" ? "Yüklenim listesi kontrol edilip tamamlanıyor…" : "Yüklenim listeleri düzenlemeye açılıyor…");
    setBusy(true); setError("");
    try {
      const result = await api<{ loading_review: LoadingReviewState }>(`/api/refund-cases/${caseId}/439-workflow`, { method: "POST", body: JSON.stringify({ action: actionName, ...values }) });
      setProgressMessage("Hesaplama sonuçları ve yüklenim listeleri yenileniyor…");
      try {
        await onChange(result.loading_review);
      } catch {
        setError("İşlem kaydedildi. Diğer ekran verileri yenilenemedi; sayfayı yenileyin. İşlemi tekrar yapmanız gerekmiyor.");
      }
      if (actionName === "calculate_loading_review_atik") {
        setServerIssues([]);
        const remaining = result.loading_review.rows.filter(row => !row.excluded.includes("atik") && row.errors.atik);
        if (remaining.length) {
          setError(`${remaining.length} ATİK satırında düzeltme gerekiyor. Diğer kayıtlar kaydedildi.`);
          return;
        }
        setAtikOpen(false);
        setChecksKind(null);
      } else if (actionName === "edit_loading_review") {
        setQuickSaves(current => {
          const next = { ...current };
          delete next[Number(values.line_id)];
          return next;
        });
        setServerIssues(current => current.filter(issue => issue.invoice_line_id !== values.line_id));
        const updated = result.loading_review.rows.find(row => row.invoice_line_id === values.line_id);
        if (updated) {
          const remaining = (["general", "atik"] as const).filter(kind => Number(updated.parts?.[kind]) > 0 && !updated.excluded.includes(kind) && updated.errors[kind]);
          if (remaining.length) {
            setEditing(updated);
            setError(`Bilgiler kaydedildi; inceleme henüz tamamlanmadı. ${remaining.map(kind => `${labels[kind]}: ${updated.errors[kind]}`).join(" ")}`);
            return;
          }
        }
      } else { setServerIssues([]); setChecksKind(null); setAtikOpen(false); }
      setEditing(null);
    } catch (reason) {
      if (reason instanceof ApiError && Array.isArray(reason.body.loading_review_errors)) {
        const issues = reason.body.loading_review_errors as ReviewIssue[];
        setServerIssues(issues);
        if (actionName === "confirm_loading_review" && (values.load_type === "general" || values.load_type === "atik")) {
          setChecksKind(values.load_type);
        } else {
          const first = review.rows.find(row => row.invoice_line_id === issues[0]?.invoice_line_id);
          if (first) open(first);
        }
      }
      setError(reason instanceof Error ? reason.message : "Karar kaydedilemedi.");
    }
    finally { setBusy(false); }
  }
  function open(row: Row, add?: "general" | "atik") {
    setError("");
    setEditing({ ...row, ...quickSaves[row.invoice_line_id]?.draft });
    setAdding(add);
  }
  const changed = review.stale || review.rows.some(row => row.stale);
  const unresolved = review.rows.filter(row => row.parts === null && quickSaves[row.invoice_line_id]?.status !== "saved");
  const atikRows = review.rows.filter(row => Number(row.parts?.atik) > 0 && !row.excluded.includes("atik"));
  function confirm(kind: "general" | "atik") {
    if (locked || changed || review.confirmed[kind]) return;
    setExcelMessage("");
    if (unresolved.length) {
      open(unresolved[0]);
      setError(`${labels[kind]} listesini tamamlamadan önce ${unresolved.length} karma satırın kaynak KDV paylarını ayırın. İlk satır aşağıda açıldı.`);
      return;
    }
    const blocked = review.rows.filter(row => Number(row.parts?.[kind]) > 0 && !row.excluded.includes(kind) && rowError(row, kind));
    if (blocked.length) {
      setError("");
      if (kind === "atik") setAtikOpen(true);
      else setChecksKind(kind);
      return;
    }
    if (onDraftsChange) { setChecksKind(kind); return; }
    setChecksKind(kind);
  }

  async function downloadChecks(kind: "general" | "atik", rows: Row[]) {
    setBusy(true); setError(""); setExcelMessage("");
    setProgressMessage("Kontrol listesi Excel olarak hazırlanıyor…");
    try {
      const query = new URLSearchParams({ load_type: kind, line_ids: rows.map(row => row.invoice_line_id).join(",") });
      const file = await apiFile(`/api/refund-cases/${caseId}/439-loading-review-excel?${query}`);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url; link.download = file.filename || "439-kontroller.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Excel indirilemedi."); }
    finally { setBusy(false); }
  }

  async function uploadChecks(kind: "general" | "atik", file: File) {
    setBusy(true); setError(""); setExcelMessage("");
    setProgressMessage(onDraftsChange ? "Excel seçimleri kontrol edilip HESAPLA için hazırlanıyor…" : "Excel kararları kontrol edilip yüklenim payları hesaplanıyor…");
    try {
      const data = new FormData();
      data.set("load_type", kind); data.set("file", file);
      if (onDraftsChange) data.set("preview_only", "true");
      const result = await api<{ updated_count: number; rows?: Record<string, unknown>[] }>(`/api/refund-cases/${caseId}/439-loading-review-excel`, { method: "POST", body: data });
      if (onDraftsChange) {
        stageRows(result.rows || []);
        setServerIssues([]);
        setChecksKind(null);
        setAtikOpen(false);
        setEditing(null);
        setExcelMessage(`${result.updated_count} seçim hazır. Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.`);
        return;
      }
      setServerIssues([]);
      await onChange();
      setExcelMessage(`${result.updated_count} satır kaydedildi. Kontrolü tamamlanan kayıtlar listeden kaldırıldı.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Excel yüklenemedi."); }
    finally { setBusy(false); }
  }
  return <section className="panel loading-review-439" id="loading-review-439">
    <div className="panel-head"><div><p className="eyebrow">439 · ÖNCE 2 VE 3 NO.LU YÜKLENİM</p><h2>Genel gider ve ATİK incelemesi</h2></div>
      {review.completed && <button type="button" className="secondary-button" disabled={locked} onClick={() => void action("reopen_loading_review")}>2 ve 3 No.lu listeleri yeniden düzenle</button>}
    </div>
    <p>Karma satırların KDV paylarını ayırıp 2 ve 3 No.lu listeleri inceleyin. Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p>
    <p>Gösterilen paylar ön hesaptır; nihai kabul edilen tutarlar 1 No.lu sonuçla güncellenir.</p>
    {Object.keys(draftRows).length > 0 && <p className="notice" role="status"><strong>{Object.keys(draftRows).length} satırın seçimi hazır.</strong> Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p>}
    {error && !editing && !checksKind && !atikOpen && <p className="issue error" role="alert">{error}</p>}
    {changed && <p className="issue warning">Kaynak kayıt değişti. Aşağıdaki “Kaynakları yenile” düğmesiyle listeleri yenileyin.</p>}
    {quickBusy && <p role="status">Seçtiğiniz satırlar arka planda kaydediliyor. Diğer satırların yüklenim grubunu seçmeye devam edebilirsiniz. Kayıtlar bitene kadar bu sayfayı açık tutun.</p>}
    {!review.completed && (unresolved.length > 0 ? <section className="panel" aria-label="Önce karma satırları ayırın">
      <h3>Önce: Karma satırları ayırın · {unresolved.length} satır bekliyor</h3>
      <p>Bu liste, KDV payları henüz belirlenmemiş fatura kalemleridir; sayı fatura adedini değil, satır adedini gösterir. Her satırın paylarını kaydedin veya tamamını kapsam dışında bırakmak için tüm payları 0 kaydedin.</p>
      <p>Bu satırlar henüz grup toplamlarına dahil değildir. Liste boşalmadan 2 ve 3 No.lu listeler tamamlanamaz.</p>
      <div style={{ maxHeight: 420, overflow: "auto" }}>
        {unresolved.map(row => {
          const saving = quickSaves[row.invoice_line_id];
          const rowLocked = disabled || busy || changed || editing?.invoice_line_id === row.invoice_line_id || Boolean(saving && saving.status !== "error");
          return <article className="panel" key={row.invoice_line_id}>
          <strong>{row.invoice_no} · {row.item_name}</strong> {viewInvoice(row)}
          <p>{row.account_codes.map(formatAccountCode).join(", ")} · {money(row.vat_amount)} kaynak KDV</p>
          <button type="button" className="secondary-button compact" disabled={rowLocked} onClick={() => open(row)}>Payları ayır</button>{" "}
          {row.account_codes.some(code => code.trim().startsWith("15")) && <><button type="button" className="secondary-button compact" disabled={rowLocked} onClick={() => includeWhole(row, "direct")}>1 No.luya dahil et</button>{" "}</>}
          <button type="button" className="secondary-button compact" disabled={rowLocked} onClick={() => includeWhole(row, "general")}>2 No.luya dahil et</button>{" "}
          <button type="button" className="secondary-button compact" disabled={rowLocked} onClick={() => includeWhole(row, "atik")}>3 No.luya dahil et</button>{" "}
          <button type="button" className="secondary-button compact" disabled={rowLocked} onClick={() => {
            const values = { line_id: row.invoice_line_id, parts: { direct: "0", general: "0", atik: "0" },
              excluded: [], period: row.invoice_date.slice(0, 7), period_allocations: null, atik_facts: {},
              explanation: "Kullanıcı karma satırın tamamını kapsam dışında bıraktı." };
            if (onDraftsChange) stageRows([values]);
            else void action("edit_loading_review", values);
          }}>Kapsam dışı bırak</button>
          {saving && <p role={saving.status === "error" ? "alert" : "status"}>{saving.message}</p>}
        </article>;
        })}
      </div>
    </section> : <p className="notice" role="status">Karma satırların pay dağılımı tamamlandı. Şimdi 2 ve 3 No.lu listelerin tamamlama kontrollerine geçebilirsiniz.</p>)}
    {!review.completed && <h3>Sonra: 2 ve 3 No.lu listeleri inceleyip tamamlayın</h3>}
    {(["general", "atik"] as const).map(kind => {
      const rows = review.rows.filter(row => Number(row.parts?.[kind] || 0) > 0);
      const active = rows.filter(row => !row.excluded.includes(kind));
      const blocked = active.filter(row => rowError(row, kind));
      const calculated = active.filter(row => !rowError(row, kind));
      const rowIds = new Set(rows.map(row => row.invoice_line_id));
      const readyCount = savedReview.rows.filter(row => draftRows[row.invoice_line_id]
        && (Number(row.parts?.[kind] || 0) > 0 || rowIds.has(row.invoice_line_id))).length;
      const awaitingSelection = kind === "atik" ? blocked.filter(row => !draftRows[row.invoice_line_id]
        && (["asset_reference", "use_start_period", "unrelieved_input_vat", "opening_prior_allocated_vat"] as const)
          .some(field => row.atik_facts?.[field] == null || row.atik_facts[field] === "")) : [];
      return <section className="panel" key={kind} aria-label={kind === "general" ? "2 No.lu genel gider" : "3 No.lu ATİK"}>
        <details className="smart-collapsible">
        <summary className="panel-head"><div><h3>{kind === "general" ? "2 No.lu — Genel gider" : "3 No.lu — ATİK"}</h3><p>{active.length} dahil satır</p></div>
          <span className="smart-collapse-meta"><span><strong>{blocked.length > 0 && calculated.length === 0 ? "Hesaplanmadı" : money(String(calculated.reduce((sum, r) => sum + Number(r.eligible[kind] || 0), 0)))}</strong><small> {blocked.length > 0 ? (calculated.length > 0 ? "Kontrolü tamamlanan satırların toplamı" : "Hesaplama bilgileri bekleniyor") : "Hesaplanan yüklenim payı"}</small></span><i aria-hidden="true">⌄</i></span>
        </summary>
        {rows.length > 0 ? <div style={{ overflowX: "auto" }}><table><thead><tr><th>Fatura / satıcı</th><th>Satır / hesap</th><th>Kaynak KDV payı</th><th>Yüklenim payı</th><th>Durum / işlem</th></tr></thead><tbody>
          {rows.map(row => <tr key={row.invoice_line_id}>
            <td>{row.invoice_no} {viewInvoice(row)}<small style={{ display: "block" }}>{row.supplier}</small></td>
            <td>{row.item_name}<small style={{ display: "block" }}>{row.account_codes.map(formatAccountCode).join(", ") || "Hesap eşleşmesi yok"}</small>
              {kind === "general" && row.general_period_explanation && <small style={{ display: "block" }}>{row.general_period_explanation}</small>}
            </td>
            <td>{money(row.parts![kind])}</td><td>{row.excluded.includes(kind) ? "—" : rowError(row, kind) ? "Hesaplanmadı" : money(row.eligible[kind] || "0")}</td>
            <td>{draftRows[row.invoice_line_id] ? "Seçim hazır; HESAPLA ile kontrol edilecek" : row.excluded.includes(kind) ? "Kapsam dışı" : rowError(row, kind) || (kind === "general" && Number(row.eligible[kind]) === 0 ? "Hesaplandı · 0 TL pay" : review.confirmed[kind] ? "Tamamlandı" : "Hesaplandı")}
              {!review.completed && <button type="button" className="secondary-button compact" disabled={locked} onClick={() => open(row)}>İncele / dahil et / çıkar</button>}</td>
          </tr>)}
        </tbody></table></div> : <p>Bu grupta aday fatura bulunmadı. Gerekirse aşağıdaki diğer faturalardan ekleyebilirsiniz.</p>}
        </details>
        {!review.completed && awaitingSelection.length > 0 && <div className="issue warning" role="status">
          <strong>{awaitingSelection.length} ATİK satırının zorunlu kullanım bilgisi eksik.</strong>
          <button type="button" className="secondary-button compact" disabled={locked || changed || unresolved.length > 0} onClick={() => { setError(""); setAtikOpen(true); }}>Eksik ATİK bilgilerini tamamla</button>
        </div>}
        {!review.completed && readyCount > 0 && <p className="notice" role="status">{labels[kind]}: {readyCount} satırın seçimi hazır. HESAPLA’ya bastığınızda kaydedilip kontrol edilecek.</p>}
        {!review.completed && !review.confirmed[kind] && (unresolved.length > 0
          ? <p>Bu listeyi tamamlamak için önce yukarıdaki karma satırların paylarını belirtin.</p>
          : null)}
        {!review.completed && <button type="button" className="primary-button" disabled={locked || changed || unresolved.length > 0 || review.confirmed[kind]} onClick={() => confirm(kind)}>
          {`${labels[kind]} seçimlerini düzenle${readyCount > 0 ? ` · ${readyCount} seçim hazır` : ""}`}
        </button>}
        {kind === "atik" && !review.completed && active.length > 0 && blocked.length === 0 && <button type="button" className="secondary-button" disabled={locked || changed || unresolved.length > 0} onClick={() => { setError(""); setAtikOpen(true); }}>ATİK bilgilerini topluca düzenle</button>}
      </section>;
    })}
    {!review.completed && <details onToggle={event => setOtherOpen(event.currentTarget.open)}><summary>İsteğe bağlı: Başka fatura dahil et / ayrılmış payları değiştir</summary>
      <p>Gerekirse aşağıdaki satırları 2 veya 3 No.luya, 15 grubu hesap kodu bulunan satırları 1 No.luya dahil edin ya da mevcut paylarını değiştirin. Payları henüz belirlenmemiş karma satırlar yalnız yukarıdaki listede yer alır.</p>
      {otherOpen && <div style={{ maxHeight: 420, overflow: "auto" }}>
        {review.rows.filter(row => row.parts !== null).map(row => <article className="panel" key={row.invoice_line_id}>
          <strong>{row.invoice_no} · {row.item_name}</strong> {viewInvoice(row)}<p>{row.account_codes.map(formatAccountCode).join(", ")} · {money(row.vat_amount)} kaynak KDV</p>
          <button type="button" className="secondary-button compact" disabled={locked} onClick={() => open(row)}>Payları düzenle</button>{" "}
          {row.account_codes.some(code => code.trim().startsWith("15")) && <><button type="button" className="secondary-button compact" disabled={locked || changed} onClick={() => includeWhole(row, "direct")}>1 No.luya dahil et</button>{" "}</>}
          <button type="button" className="secondary-button compact" disabled={locked} onClick={() => open(row, "general")}>2 No.luya dahil et</button>{" "}
          <button type="button" className="secondary-button compact" disabled={locked} onClick={() => open(row, "atik")}>3 No.luya dahil et</button>
        </article>)}
      </div>}
    </details>}
    {checksKind && !review.completed && <LoadingReviewChecks label={labels[checksKind]} locked={locked || changed} error={error} message={excelMessage}
      rows={review.rows.filter(row => !row.excluded.includes(checksKind) && (row.parts === null || Number(row.parts[checksKind]) > 0) && rowError(row, checksKind))}
      rowError={row => rowError(row, checksKind)} onClose={() => { setChecksKind(null); setError(""); }}
      onEdit={row => open(row)} onPreview={row => setPreview(row)}
      onExclude={row => {
        const values = { line_id: row.invoice_line_id, parts: row.parts,
          excluded: [...new Set([...row.excluded, checksKind])], period: row.period,
          explanation: row.explanation || "Kullanıcı ilgili yüklenim grubunu kapsam dışında bıraktı." };
        if (onDraftsChange) { stageRows([values]); setChecksKind(null); }
        else void action("edit_loading_review", values);
      }}
      onDownload={rows => void downloadChecks(checksKind, rows)} onUpload={file => void uploadChecks(checksKind, file)}
      canConfirm={unresolved.length === 0} saveOnCalculate={Boolean(onDraftsChange)} onConfirm={() => confirm(checksKind)} />}
    {atikOpen && atikRows.length > 0 && !review.completed && <AtikReviewDialog rows={atikRows} periodStart={review.period_start || ""} locked={locked || changed || unresolved.length > 0} error={error}
      onDownload={rows => void downloadChecks("atik", rows)} onUpload={file => void uploadChecks("atik", file)}
      onClose={() => { setAtikOpen(false); setError(""); }} onPreview={row => setPreview(row)} onEdit={row => open(row)}
      saveOnCalculate={Boolean(onDraftsChange)} onSave={rows => void action("calculate_loading_review_atik", { rows })} />}
    {editing && !review.completed && <LoadingReviewEditor key={editing.invoice_line_id} editing={editing} add={adding} periodStart={review.period_start || ""} locked={disabled || busy} error={error}
      errors={{ general: rowError(editing, "general"), atik: rowError(editing, "atik") }}
      onClose={() => setEditing(null)} onPreview={() => setPreview(editing)} onDraftChange={onDraftsChange ? values => stageRows([values]) : undefined} onSave={saveEditor} />}
    {preview && <InvoicePreviewModal invoice={{ id: preview.invoice_id, invoice_no: preview.invoice_no }} showAccountingEntries onClose={() => setPreview(null)} />}
    <FullscreenProgress visible={busy} message={progressMessage} modal />
    {review.completed && <p role="status">2 ve 3 No.lu inceleme tamamlandı. 1 No.lu doğrudan yüklenimin hesaplama durumu aşağıda gösteriliyor.</p>}
  </section>;
}

const atikFactKeys = ["asset_reference", "use_start_period", "unrelieved_input_vat", "opening_prior_allocated_vat", "opening_cumulative_refund_turnover", "opening_cumulative_total_turnover"] as const;

function atikDraft(rows: Row[], periodStart: string): AtikDraft {
  const draft = initialAtikDraft(rows.map(row => ({ key: String(row.invoice_line_id), label: `${row.invoice_no} · ${row.item_name}`, source_vat: row.parts?.atik || "0", invoice_month: row.invoice_date.slice(0, 7) })), undefined, periodStart);
  for (const row of rows) {
    const key = String(row.invoice_line_id);
    draft[key] = atikFactKeys.map((field, index) => row.atik_facts?.[field] ?? draft[key][index]);
  }
  return draft;
}

function AtikReviewDialog({ rows, periodStart, locked, error, onClose, onPreview, onEdit, onSave, onDownload, onUpload, saveOnCalculate = false }: {
  saveOnCalculate?: boolean; rows: Row[]; periodStart: string; locked: boolean; error: string; onClose: () => void;
  onPreview: (row: Row) => void; onEdit: (row: Row) => void; onSave: (rows: Record<string, unknown>[]) => void;
  onDownload: (rows: Row[]) => void; onUpload: (file: File) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const excelInput = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    const element = dialog.current!;
    const focus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    element.showModal();
    return () => {
      element.close();
      document.body.style.overflow = overflow;
      if (focus instanceof HTMLElement && focus.isConnected) focus.focus({ preventScroll: true });
    };
  }, []);
  return <dialog ref={dialog} className="panel loading-review-dialog atik-bulk-dialog" aria-label="ATİK’leri topluca hesapla" onCancel={event => { event.preventDefault(); if (!locked) onClose(); }}>
    <div className="panel-head"><h3>3 No.lu · {rows.length} ATİK satırını incele</h3><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      <button type="button" className="secondary-button compact" disabled={locked} onClick={() => onDownload(rows)}>Excel indir</button>
      <button type="button" className="secondary-button compact" disabled={locked} onClick={() => excelInput.current?.click()}>Excel yükle</button>
      <input ref={excelInput} hidden type="file" accept=".xlsx" onChange={event => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (file) onUpload(file); }} />
      <button type="button" className="secondary-button compact" disabled={locked} onClick={onClose}>Kapat</button>
    </div></div>
    {error && <p className="issue warning" role="alert">{error}</p>}
    <p>Excel’de varlık referansı, kullanım ayı, giderilemeyen KDV ve önceki dönem tutarlarını kontrol edin. İşlem sütununda “ATİK bilgilerini doğrula” veya “Kapsam dışı bırak” seçin. Önerilen bilgiler doğrulanmadan uygulanmaz.</p>
    <AtikQuestion question={{ title: "ATİK", facts: { rows: rows.map(row => ({ key: String(row.invoice_line_id), label: `${row.invoice_no} · ${row.item_name}`, source_vat: row.parts!.atik, invoice_month: row.invoice_date.slice(0, 7) })) } }}
      periodStart={periodStart} disabled={locked} saveOnCalculate={saveOnCalculate}
      onDraftChange={answer => {
        if (!answer.startsWith("custom:")) {
          if (answer === "exclude") onSave(rows.map(row => ({line_id: row.invoice_line_id, parts: row.parts,
            excluded: [...new Set([...row.excluded, "atik"])], period: row.period,
            explanation: "Kullanıcı ATİK payını kapsam dışında bıraktı."})));
          return;
        }
        const draft = JSON.parse(answer.slice(7)) as AtikDraft;
        onSave(rows.map(row => ({ line_id: row.invoice_line_id, parts: row.parts, excluded: row.excluded,
          period: row.period, explanation: row.explanation, atik_facts: {
          ...Object.fromEntries(atikFactKeys.map((key, index) => [key, draft[String(row.invoice_line_id)][index]])), turnover_scope_confirmed: true } })));
      }}
      review={{ initial: atikDraft(rows, periodStart),
        errors: Object.fromEntries(rows.filter(row => row.atik_facts?.turnover_scope_confirmed && row.errors.atik).map(row => [String(row.invoice_line_id), row.errors.atik])),
        onPreview: key => onPreview(rows.find(row => String(row.invoice_line_id) === key)!),
        onEdit: key => onEdit(rows.find(row => String(row.invoice_line_id) === key)!),
      }}
      onAnswer={answer => {
        const draft = JSON.parse(answer.slice(7)) as AtikDraft;
        onSave(rows.map(row => ({ line_id: row.invoice_line_id, atik_facts: { ...Object.fromEntries(atikFactKeys.map((key, index) => [key, draft[String(row.invoice_line_id)][index]])), turnover_scope_confirmed: true } })));
      }} />
  </dialog>;
}

function LoadingReviewChecks({ label, rows, rowError, locked, error, message, canConfirm, onClose, onEdit, onExclude, onPreview, onConfirm, onDownload, onUpload, saveOnCalculate = false }: {
  saveOnCalculate?: boolean; label: string; rows: Row[]; rowError: (row: Row) => string; locked: boolean; error: string; message: string; canConfirm: boolean;
  onClose: () => void; onEdit: (row: Row) => void; onPreview: (row: Row) => void; onConfirm: () => void;
  onExclude: (row: Row) => void;
  onDownload: (rows: Row[]) => void; onUpload: (file: File) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const excelInput = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    const element = dialog.current!;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    element.showModal();
    return () => {
      element.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);
  return <dialog ref={dialog} className="panel loading-review-dialog" aria-label={`${label} tamamlama kontrolleri`}
    onCancel={event => { event.preventDefault(); if (!locked) onClose(); }}>
    <div className="panel-head"><h3>{label} tamamlama kontrolleri · {rows.length} satır</h3><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      <button type="button" className="secondary-button compact" disabled={locked || !rows.length} onClick={() => onDownload(rows)}>Excel indir</button>
      <button type="button" className="secondary-button compact" disabled={locked} onClick={() => excelInput.current?.click()}>Excel yükle</button>
      <input ref={excelInput} hidden type="file" accept=".xlsx" onChange={event => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (file) onUpload(file); }} />
      <button type="button" className="secondary-button compact" disabled={locked} onClick={onClose}>Kapat</button>
    </div></div>
    <p>Pay ayrıştırması ile hesaplama kontrolü farklı adımlardır. Aşağıdaki kayıtların bilgilerini düzeltin veya ilgili gruptan çıkarın. Tamamlanan kayıtlar bu listeden kalkar.</p>
    <p>Excel’de yalnız İşlem sütunundan 1, 2 veya 3 No.lu kaynak KDV grubunu seçin. Sistem kaynak KDV payını seçilen gruba aktarır ve tutarları hesaplar. Boş bıraktığınız satırlar değişmez.</p>
    {error && <p className="issue error" role="alert">{error}</p>}
    {message && <p className="notice" role="status">{message}</p>}
    {rows.map(row => <article className="panel" key={row.invoice_line_id}>
      <strong>{row.invoice_no} · {row.item_name}</strong>{" "}<button type="button" className="text-button loading-review-view" onClick={() => onPreview(row)}>Gör</button>
      <p className="issue warning">{rowError(row)}</p>
      <button type="button" className="secondary-button compact" disabled={locked} onClick={() => onEdit(row)}>İncele / dahil et / çıkar</button>
      <button type="button" className="secondary-button compact" disabled={locked} onClick={() => onExclude(row)}>{label} kapsamı dışında bırak</button>
    </article>)}
    {!rows.length && <><p className="notice" role="status">Hesaplama kontrolleri tamamlandı. Listeyi tamamlayabilirsiniz.</p>
      {saveOnCalculate ? <p>Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p> : <button type="button" className="primary-button" disabled={locked || !canConfirm} onClick={onConfirm}>{label} listesini tamamla</button>}</>}
  </dialog>;
}


type EditorProps = {
  editing: Row; add?: "general" | "atik"; periodStart: string; locked: boolean; error: string;
  errors: Record<string, string>; onClose: () => void; onPreview: () => void;
  onDraftChange?: (values: Record<string, unknown>) => void;
  onSave: (values: Record<string, unknown>) => void;
};

function LoadingReviewEditor({ editing, add, periodStart, locked, error, errors, onClose, onPreview, onSave, onDraftChange }: EditorProps) {
  const initialParts = { ...(editing.parts || { direct: "0", general: "0", atik: "0" }) };
  if (add) { initialParts.direct = "0"; initialParts.general = "0"; initialParts.atik = "0"; initialParts[add] = editing.vat_amount; }
  const [parts, setParts] = useState<Parts>(initialParts);
  const [facts, setFacts] = useState<AssetFacts>(() => ({ ...Object.fromEntries(atikFactKeys.map((key, index) => {
    const cell = atikDraft([{ ...editing, parts: initialParts }], periodStart)[String(editing.invoice_line_id)][index];
    return [key, index < 2 ? cell : parseMoneyInput(cell) ?? ""];
  })), turnover_scope_confirmed: false, ...editing.atik_facts }));
  const [periods, setPeriods] = useState(!add && editing.period_allocations?.length ? editing.period_allocations : !add && editing.automatic_general_periods?.length ? editing.automatic_general_periods : [{ period: editing.period, source_vat_share: initialParts.general }]);
  const [lateDocument, setLateDocument] = useState<LateDocumentFacts>({
    late_document_kind: editing.late_document_kind || "",
    underlying_supply_date: editing.underlying_supply_date || "",
    correction_ack: editing.correction_ack === true,
    late_document_explanation: editing.late_document_explanation || "",
  });
  const [excluded, setExcluded] = useState(add ? editing.excluded.filter(kind => kind !== add) : editing.excluded);
  const [explanation, setExplanation] = useState(editing.explanation || "");
  const [directQuantity, setDirectQuantity] = useState(editing.direct_quantity || editing.quantity);
  const [splitError, setSplitError] = useState("");
  const editor = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    const dialog = editor.current;
    if (!dialog) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
    };
  }, []);
  const draftPayload = JSON.stringify({ line_id: editing.invoice_line_id, parts: Object.fromEntries(Object.entries(parts).map(([kind, value]) => [kind, parseMoneyInput(value || "0") ?? value])), excluded, explanation,
    direct_quantity: directQuantity, period: periods[0]?.period || editing.period,
    period_allocations: Number(parts.general) > 0 && !excluded.includes("general") ? periods : null,
    ...lateDocument, atik_facts: Number(parts.atik) > 0 ? facts : {} });
  useEffect(() => { onDraftChange?.(JSON.parse(draftPayload)); }, [draftPayload]);
  const labels = { direct: "1 No.lu", general: "2 No.lu", atik: "3 No.lu" };
  function save() {
    const parsed = (["direct", "general", "atik"] as const).map(kind => parseMoneyInput(parts[kind].trim() || "0"));
    if (parsed.some(value => value === null)) {
      setSplitError("Kaynak KDV paylarına geçerli tutarlar girin. Boş bırakılan paylar sıfır kabul edilir.");
      return;
    }
    const source = parseMoneyInput(editing.vat_amount);
    const total = parsed.reduce((sum, value) => sum + BigInt(value!.replace(".", "")), 0n);
    if (source === null || total > BigInt(source.replace(".", ""))) {
      setSplitError("Payların toplamı bu kalemin kaynak KDV’sini aşamaz.");
      return;
    }
    const normalized = { direct: parsed[0]!, general: parsed[1]!, atik: parsed[2]! };
    setSplitError("");
    setParts(normalized);
    onSave({ line_id: editing.invoice_line_id, parts: normalized, excluded, explanation,
      direct_quantity: directQuantity, period: periods[0]?.period || editing.period,
      period_allocations: Number(normalized.general) > 0 && !excluded.includes("general") ? periods : null,
      ...lateDocument, atik_facts: Number(normalized.atik) > 0 ? facts : {} });
  }
  return <dialog ref={editor} className="panel loading-review-dialog" aria-labelledby="loading-review-title" onCancel={event => { event.preventDefault(); if (!locked) onClose(); }}>
      <div className="panel-head"><h3 id="loading-review-title">{editing.invoice_no} · {editing.item_name} <button type="button" className="text-button loading-review-view" aria-label={`${editing.invoice_no} faturasını gör`} onClick={onPreview}>Gör</button></h3><button type="button" className="secondary-button compact" aria-label="Düzenlemeyi kapat" disabled={locked} onClick={() => onClose()}>Kapat</button></div>
      <p>Doğrulanmış kaynak KDV: {money(editing.vat_amount)}. Dağıtılmayan pay kapsam dışında kalır.</p>
      <p>Payları yüzde olarak değil, TL tutarı olarak girin. Boş bırakılan paylar sıfır kabul edilir. 1, 2 ve 3 No.lu tutarların toplamı doğrulanmış kaynak KDV’yi aşamaz.</p>
      <p>Muhasebe hesapları: {editing.account_codes.map(formatAccountCode).join(", ") || "Hesap eşleşmesi yok"}</p>
      {Number(parts.atik) > 0 && !excluded.includes("atik") && <>
        {Boolean(editing.atik_calculation?.length) && <details className="atik-calculation-guide">
          <summary>Son kaydedilen ATİK paylarının hesabını göster</summary>
          <p>Hesapta kullanılan giderilemeyen ATİK KDV’si: {money(editing.atik_calculation![0].asset_input_vat)}. Aşağıdaki tutarlar son kayda aittir; bu formdaki değişiklikler kaydedilince güncellenir. Pay ayrılmayan aylar listelenmeyebilir.</p>
          {editing.stale && <p className="issue warning">Kaynaklar değiştiği için bu hesap güncel değildir; kaynakları yenileyin.</p>}
          <div style={{ overflowX: "auto" }}><table><thead><tr><th>Ay</th><th>Kümülatif işlem bedelleri / oran</th><th>Kümülatif pay sınırı</th><th>Önceden verilen toplam pay</th><th>Diğer yüklenimlerden sonra kalan sınır</th><th>Bu ay ayrılan pay</th></tr></thead><tbody>
            {editing.atik_calculation!.map(item => <tr key={item.period}>
              <td style={{ whiteSpace: "nowrap" }}>{item.period}</td>
              <td>{money(item.cumulative_refund_turnover)} / {money(item.cumulative_total_turnover)}<br />%{(Number(item.cumulative_ratio) * 100).toLocaleString("tr-TR", { maximumFractionDigits: 4 })}</td>
              <td>{money(item.cumulative_eligible_vat)}</td><td>{money(item.prior_allocated_vat)}</td>
              <td>{money(item.available_room_after_other_atik)}</td><td>{money(item.eligible_vat)}</td>
            </tr>)}
          </tbody></table></div>
        </details>}
      </>}
      {editing.parts === null && <p className="issue warning">Kaynak KDV'nin yüklenim gruplarına dağılımı otomatik belirlenemedi. Muhasebe kaydına göre 1, 2 ve 3 No.lu payları girin. Tüm payları 0 kaydetmek bu satırın tamamını kapsam dışında bırakır.</p>}
      {!error && (["general", "atik"] as const).filter(kind => Number(parts[kind]) > 0 && !excluded.includes(kind) && errors[kind]).map(kind => <p key={kind} className="issue warning">{labels[kind]}: {errors[kind]}</p>)}
      {error && <p className="issue error" role="alert">{error}</p>}
      <fieldset className="loading-review-fields" disabled={locked}>
      {(Object.keys(labels) as LoadType[]).map(kind => <label key={kind}>{labels[kind]} kaynak KDV payı (TL)<MoneyInput locale="tr-TR" value={parts[kind]} onChange={value => {
        setParts({ ...parts, [kind]: value });
        if (kind === "general" && periods.length === 1) setPeriods([{ ...periods[0], source_vat_share: value || "0" }]);
        setSplitError("");
      }} /></label>)}
      {splitError && <p className="issue error" role="alert">{splitError}</p>}
      {Number(parts.direct) > 0 && <fieldset><legend>1 No.lu gider dönemleri</legend>
        <p>1 No.lu yüklenim dönemi, 2 ve 3 No.lu listeler tamamlandıktan sonra doğrudan yüklenim aşamasında belirlenir.</p>
      {Number(parts.direct) < Number(editing.vat_amount) && <label>1 No.luya ayrılan miktar ({editing.unit}; kaynak {editing.quantity})<input type="number" min="0" step="any" value={directQuantity} onChange={e => setDirectQuantity(e.target.value)} /></label>}
      </fieldset>}
      {(["general", "atik"] as const).filter(kind => Number(parts[kind]) > 0).map(kind => <label key={kind}><input type="checkbox" checked={!excluded.includes(kind)} onChange={e => setExcluded(e.target.checked ? excluded.filter(t => t !== kind) : [...excluded, kind])} />{labels[kind]} kapsamına dahil et (diğer paylar korunur)</label>)}
      {Number(parts.general) > 0 && !excluded.includes("general") && <fieldset><legend>2 No.lu gider dönemleri</legend>
        {periods.map((period, i) => <div key={i}><label>Yüklenim ayı<input type="month" value={period.period} onChange={e => { setPeriods(periods.map((p, n) => n === i ? { ...p, period: e.target.value } : p)); setLateDocument({ ...lateDocument, correction_ack: false }); }} /></label>
          <label>Aya ayrılan kaynak KDV (TL)<MoneyInput locale="tr-TR" value={period.source_vat_share} onChange={value => setPeriods(periods.map((p, n) => n === i ? { ...p, source_vat_share: value } : p))} /></label>
          {periods.length > 1 && <button type="button" onClick={() => setPeriods(periods.filter((_, n) => n !== i))}>Ayı kaldır</button>}</div>)}
        <button type="button" className="secondary-button compact" onClick={() => setPeriods([...periods, { period: "", source_vat_share: "0" }])}>Başka aya pay ayır</button>
        <fieldset><legend>Geç belge bilgileri</legend>
          <p>Yüklenim ayı KDV indirim ayından önceyse bu bilgiler gerekir. Belgeye dayanan ifa tarihini ve geç belge nedenini girin; düzeltme onayı verilmeden geçmiş aya yüklenim hesaplanmaz.</p>
          {editing.deduction_period && <p>Doğrulanmış KDV indirim ayı: {editing.deduction_period.slice(0, 7)}</p>}
          <label>Geç belge durumu<select value={lateDocument.late_document_kind} onChange={e => setLateDocument({ ...lateDocument, late_document_kind: e.target.value, correction_ack: false })}>
            <option value="">Seçin</option>
            <option value="issued_after_sale">Belge ilgili satış ayından sonra düzenlendi</option>
            <option value="obtained_after_sale">Belge ilgili satış ayından sonra temin edildi</option>
          </select></label>
          <label>Mal teslimi / hizmet ifa tarihi<input type="date" value={lateDocument.underlying_supply_date} onChange={e => setLateDocument({ ...lateDocument, underlying_supply_date: e.target.value, correction_ack: false })} /></label>
          <label>Geç belge açıklaması<textarea value={lateDocument.late_document_explanation} onChange={e => setLateDocument({ ...lateDocument, late_document_explanation: e.target.value, correction_ack: false })} /></label>
          <label><input type="checkbox" checked={lateDocument.correction_ack === true} onChange={e => setLateDocument({ ...lateDocument, correction_ack: e.target.checked })} />Geç belge bilgilerinin doğruluğunu ve ilgili satış ayına düzeltme yapılmasını onaylıyorum.</label>
        </fieldset>
      </fieldset>}
      {Number(parts.atik) > 0 && !excluded.includes("atik") && <fieldset><legend>3 No.lu gider dönemleri / kullanım bilgileri</legend>
        <label>Varlık referansı<input value={facts.asset_reference || ""} onChange={e => setFacts({ ...facts, asset_reference: e.target.value })} /></label>
        <label>Kullanıma başlama ayı<input type="month" value={facts.use_start_period || ""} onChange={e => setFacts({ ...facts, use_start_period: e.target.value, turnover_scope_confirmed: false })} /></label>
        <label>Giderilemeyen ATİK KDV’si (TL)<MoneyInput locale="tr-TR" value={facts.unrelieved_input_vat || ""} onChange={value => setFacts({ ...facts, unrelieved_input_vat: value })} /></label>
        <details><summary>Önceki dönem bilgilerini düzenle</summary>
          {([
            ["opening_prior_allocated_vat", "VRP dışında önceki kullanılan KDV"],
            ["opening_cumulative_refund_turnover", "Dosya dönemi öncesi ilgili işlem bedeli"],
            ["opening_cumulative_total_turnover", "Dosya dönemi öncesi toplam işlem bedeli"],
          ] as const).map(([key, label]) => <label key={key}>{label} (TL)<MoneyInput locale="tr-TR" value={facts[key] || ""} onChange={value => setFacts({ ...facts, [key]: value })} /></label>)}
        </details>
      </fieldset>}
      <label>Kararın gerekçesi / giderin indirimli oranlı faaliyetle ilişkisi (isteğe bağlı)<textarea value={explanation} onChange={e => setExplanation(e.target.value)} /></label>
      {onDraftChange ? <p>Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p> : <button type="button" className="primary-button" disabled={locked} onClick={save}>Kaydet ve payları hesapla</button>}{" "}
      <button type="button" className="secondary-button" disabled={locked} onClick={() => onClose()}>Vazgeç</button>
      </fieldset>
    </dialog>;
}
